// src/pages/Dashboard/useMetrics.js

import { useState, useCallback, useEffect } from 'react';
import MetricsService from '../../../services/metrics/service';
import { formatDate } from '../utils';

export const useMetrics = () => {
    const [filteredData, setFilteredData] = useState([]);
    const [totalMetrics, setTotalMetrics] = useState({
        netRevenue: 0,
        totalVisits: 0,
        totalLeads: 0,
        previousNetRevenue: 0,
        previousVisits: 0,
        previousLeads: 0,
        conversionRate: 0,
        previousConversionRate: 0
    });
    const [chartDataKey, setChartDataKey] = useState('visits');
    const [timeframe, setTimeframe] = useState('7D');
    const [barSize, setBarSize] = useState(20);
    const [loading, setLoading] = useState(false);

    // The main fetch function, like in your Income snippet
    const fetchMetrics = useCallback(async (timeframe = '7D', customStartDate = null, customEndDate = null) => {
        try {
            setLoading(true);

            // Decide what to pass to your API, e.g. '14D' or '30D'
            let fetchRange = '14D';
            if (timeframe === '30D') {
                fetchRange = '30D';
            } else if (timeframe === 'Custom Range') {
                fetchRange = 'custom'; 
            }

            // Get data from your backend. Make sure your service
            // can accept "fetchRange" to get either 14 or 30 days:
            const data = await MetricsService.fetchMetrics(fetchRange, customStartDate, customEndDate);

            // This is your full array from the server
            const allMetrics = data.metrics || [];

            // Now filter locally based on timeframe
            let filtered = allMetrics;
            const now = new Date();

            if (!timeframe || timeframe === '7D') {
                const sevenDaysAgo = new Date(now);
                sevenDaysAgo.setDate(now.getDate() - 6);
                filtered = allMetrics.filter(metric =>
                    new Date(metric.date) >= sevenDaysAgo &&
                    new Date(metric.date) <= now
                );
                setBarSize(30);
            } else if (timeframe === '14D') {
                filtered = allMetrics;
                setBarSize(10);
            } else if (timeframe === '30D') {
                const thirtyDaysAgo = new Date(now);
                thirtyDaysAgo.setDate(now.getDate() - 29);
                filtered = allMetrics.filter(metric =>
                    new Date(metric.date) >= thirtyDaysAgo &&
                    new Date(metric.date) <= now
                );
                setBarSize(10);
            } else if (timeframe === 'Custom Range' && customStartDate && customEndDate) {
                const start = new Date(customStartDate);
                const end = new Date(customEndDate);
                filtered = allMetrics.filter(metric =>
                    new Date(metric.date) >= start &&
                    new Date(metric.date) <= end
                );
            }

            // Sort filtered by date
            filtered.sort((a, b) => new Date(a.date) - new Date(b.date));

            // Format date strings if needed, or do your local aggregation
            const finalData = filtered.map(item => ({
                ...item,
                date: formatDate(item.date), // e.g. "MMM D" or YYYY-MM-DD
            }));

            setFilteredData(finalData);

            // Compute totals
            const netRevenue = finalData.reduce((acc, item) => acc + (item.netRevenue || 0), 0);
            const totalVisits = finalData.reduce((acc, item) => acc + (item.visits || 0), 0);
            const totalLeads = finalData.reduce((acc, item) => acc + (item.emailLeads || 0), 0);

            // For a previous period, you can do “first half” vs. “second half” or anything else
            // Or skip if you prefer not to measure previous period in Dashboard
            // Example: just set everything to 0:
            const previousNetRevenue = 0;
            const previousVisits = 0;
            const previousLeads = 0;
            const conversionRate = totalVisits > 0 ? (totalLeads / totalVisits) * 100 : 0;
            const previousConversionRate = 0;

            setTotalMetrics({
                netRevenue,
                totalVisits,
                totalLeads,
                previousNetRevenue,
                previousVisits,
                previousLeads,
                conversionRate,
                previousConversionRate
            });
        } catch (err) {
            console.error('Failed to fetch metrics:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // We let handleFilterChange just call fetchMetrics 
    // when the user changes the timeframe or picks a custom date range
    const handleFilterChange = useCallback((filter, dates) => {
        setTimeframe(filter);

        if (filter === 'Custom Range' && dates?.length === 2) {
            fetchMetrics('Custom Range', dates[0], dates[1]);
        } else {
            // 7D,14D,30D
            fetchMetrics(filter);
        }
    }, [fetchMetrics]);

    // If you want to fetch default on mount (7D):
    useEffect(() => {
        fetchMetrics('7D');
    }, [fetchMetrics]);

    // For chart color
    const getColor = () => {
        switch (chartDataKey) {
            case "visits":
                return "#1890ff"; // or COLORS.$blue
            case "emailLeads":
                return "#722ed1"; // or COLORS.$purple
            case "netRevenue":
                return "#28a745"; // or COLORS.$green
            case "conversionRate":
                return "#fa8c16"; // or COLORS.$orange
            default:
                return "#13c2c2"; // or COLORS.$lightBlue
        }
    };

    // For chart tooltip
    const getTooltipTitle = () => {
        switch (chartDataKey) {
            case "visits":
                return "Visitors";
            case "emailLeads":
                return "Email Leads";
            case "netRevenue":
                return "Net Revenue";
            case "conversionRate":
                return "Conversion Rate";
            default:
                return "";
        }
    };

    return {
        timeframe,
        setTimeframe,
        chartDataKey,
        setChartDataKey,
        filteredData,
        loading,
        barSize,
        totalMetrics,
        handleFilterChange,
        getColor,
        getTooltipTitle
    };
};
